import {
  GetWebsitesBlockedResponse,
  IntValueWithDelta,
  Status,
  WebsitesCategories,
  WebsiteCategory,
} from '@nirvana/shared/dtos/types/widgets/common';
import { DisplayMode, TemplateState } from '../../types/common/types';
import { BackendResponseWithMeta, DASHBOARD_METADATA_KEY } from '../../types/widget/types';
import { Target } from '@nirvana/shared/dtos/types/dashboards/common';
import {
  CommonThreatsNavigationHelper,
  NavigationItem,
  WebsitesBlockedNavigationHelper,
} from '@nirvana/shared/navigation';
import { BaseThreatWidgetView } from '../../widget/BaseThreatWidgetView';
import { GzProgressWebComponent } from '../../ui/gzProgress';

export class WebsitesBlockedView extends BaseThreatWidgetView {
  private static readonly displayedCategories: Array<string> = ['malware', 'phishing', 'fraud'];
  public latestData: any;
  public shadow: any;
  protected translations: any;
  private $style: any;
  private chartColors: Array<string>;
  private $detailsList: HTMLElement[];

  getDomElements() {
    super.getDomElements();
    this.dom.$widgetTitle = this.shadow.querySelector('.widget-title');
    this.dom.$widgetTitleString = this.shadow.querySelector('#websitesBlockedTitle');
    this.dom.$gzProgressBarList = this.shadow.querySelectorAll('.gz-progress');
    this.$detailsList = this.shadow.querySelectorAll('.item-details');
    this.$style = getComputedStyle(document.body);
    this.getTranslations();
    this.setTranslatedStrings();
    this.getChartColors();
    this.setErrorTooltip();
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getWebsitesBlockedWidgetStrings();
  }

  setTranslatedStrings() {
    const { title } = this.translations;
    this.dom.$widgetTitleString.innerHTML = title;
  }

  private getChartColors() {
    const websitesProgressColor = this.$style.getPropertyValue('--blue-chart-500');
    this.chartColors = [websitesProgressColor.trimLeft()];
  }

  buildSuccessView(response: BackendResponseWithMeta<GetWebsitesBlockedResponse>) {
    this.resetTemplateState(this.$wrapper);

    const result = response.widgetResult;

    if (result.status === Status.EMPTY_STATE) {
      this.setTemplateState(this.$wrapper, TemplateState.EMPTY);
      this.buildEmptyTemplate();
      return;
    }

    if (result.status === Status.ACTIVE && result.data) {
      this.latestData = result;
      this.buildNameList(WebsitesBlockedView.displayedCategories);

      switch (response[DASHBOARD_METADATA_KEY].displayMode) {
        case DisplayMode.PDF:
          Array.from(this.dom.$gzProgressBarList).map((progressBar: GzProgressWebComponent) => {
            progressBar.animations = false;
          });
          break;
        case DisplayMode.DASHBOARD:
        case DisplayMode.REPORT:
          Array.from(this.dom.$gzProgressBarList).map((progressBar: GzProgressWebComponent) => {
            progressBar.animations = true;
          });
          break;
        default:
          throw new Error('Should not reach');
      }

      this.buildValuesAndDeltaList(this.latestData.data, this.latestData.total);
      this.addDrillDownHandlers(
        response[DASHBOARD_METADATA_KEY].targets,
        response[DASHBOARD_METADATA_KEY].reportingPeriod.timeRange,
        response[DASHBOARD_METADATA_KEY].canDrillDown
      );
      return;
    }

    this.buildErrorView();
  }

  buildErrorView() {
    this.resetTemplateState(this.$wrapper);
    this.setTemplateState(this.$wrapper, TemplateState.ERROR);
    this.buildEmptyTemplate();
  }

  private buildNameList(categoryNames: Array<string>) {
    categoryNames.forEach((name: string) => {
      this.buildNameListElement(name);
    });
  }

  private buildNameListElement(name: string) {
    const { categoriesMap } = this.translations;
    const categoryName = categoriesMap[name];
    const $websiteCategory = this.shadow.querySelector(`[gz-list-element="${name}"]`);
    if (!$websiteCategory) {
      return;
    }

    $websiteCategory.textContent = categoryName;
  }

  private buildValuesAndDeltaList(categories: WebsitesCategories, total: number) {
    const valuesAndDeltas = WebsitesBlockedView.displayedCategories.map((websiteCategory: string) => {
      return {
        currentValue: categories[websiteCategory].values.currentValue,
        delta: categories[websiteCategory].values.delta,
      };
    });

    valuesAndDeltas.forEach((data: IntValueWithDelta, index: number) => {
      const categoryName = WebsitesBlockedView.displayedCategories[index];

      this.buildValuesAndDeltaListElement(data.currentValue, data.delta, categoryName);
      this.buildProgressBarElement(data.currentValue, total, categoryName);
    });
  }

  private buildValuesAndDeltaListElement(value: number, delta: number, categoryName: string) {
    const $categoryValue = this.shadow.querySelector(`[gz-list-element="${categoryName}Value"]`);
    // @ts-ignore
    $categoryValue.textContent = value;
    const $categoryDelta = this.shadow.querySelector(`[gz-list-element="${categoryName}Delta"]`);

    this.displayDeltaInElement($categoryDelta, delta, false);
  }

  private buildProgressBarElement(value: number, total: number, categoryName: string) {
    const $progressBarLine = this.shadow.querySelector(`[gz-list-element="${categoryName}ProgressBar"]`);
    // @ts-ignore
    $progressBarLine.setAttribute('valueNow', value);
    // @ts-ignore
    $progressBarLine.setAttribute('valueMax', total);
    $progressBarLine.setAttribute('progressColor', this.chartColors[0]);
  }

  private buildEmptyTemplate() {
    const categories = {
      fraud: {
        values: {
          currentValue: 0,
          delta: 0,
        },
        threatType: -1,
      },
      malware: {
        values: {
          currentValue: 0,
          delta: 0,
        },
        threatType: -1,
      },
      phishing: {
        values: {
          currentValue: 0,
          delta: 0,
        },
        threatType: -1,
      },
    };

    const total = 0;

    this.buildNameList(WebsitesBlockedView.displayedCategories);
    this.buildValuesAndDeltaList(categories, total);
  }

  protected getNavigationItemForTitle(targets: Array<Target>, timeRange: number): NavigationItem {
    const isPartner = Application.Main.company.isPartner();
    const filters = WebsitesBlockedNavigationHelper.getFiltersForNavigationItem({
      itemKey: 'title',
      timeRange: timeRange,
      targets: targets,
      isPartner: isPartner,
      threatTypes: [],
    });
    const displayedColumns = WebsitesBlockedNavigationHelper.getDisplayedColumns(false);

    return CommonThreatsNavigationHelper.buildNavigationItem([this.dom.$widgetTitle], filters, displayedColumns);
  }

  protected getNavigationItemsForWidgetBody(targets: Array<Target>, timeRange: number): Array<NavigationItem> {
    return Object.values(this.latestData.data).map((category: WebsiteCategory, listElementIndex: number) =>
      this.buildNavigationItemForListElement(category, targets, timeRange, listElementIndex)
    );
  }

  private buildNavigationItemForListElement(
    category: WebsiteCategory,
    targets: Array<Target>,
    timeRange: number,
    elementIndex: number
  ): NavigationItem {
    const $detailsElem = this.$detailsList[elementIndex];
    const threatTypes = category.threatType ? [category.threatType] : [];
    const categoryName = Object.keys(this.latestData.data)[elementIndex];
    const isPartner = Application.Main.company.isPartner();
    const filters = WebsitesBlockedNavigationHelper.getFiltersForNavigationItem({
      itemKey: categoryName,
      timeRange: timeRange,
      targets: targets,
      isPartner: isPartner,
      threatTypes: threatTypes,
    });
    const displayedColumns = WebsitesBlockedNavigationHelper.getDisplayedColumns(!!category.threatType);

    return CommonThreatsNavigationHelper.buildNavigationItem([$detailsElem], filters, displayedColumns);
  }
}
